import {
  endOfMonth,
  format,
  startOfMonth,
  startOfYear,
  subMonths,
  subWeeks,
} from "date-fns";
import { CalendarDays, Check, ChevronDown, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import { dateFnsLocale } from "@/v2/utils/format-date";
import { Button } from "@/v2/ui/primitives/button";
import { Calendar } from "@/v2/ui/primitives/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/v2/ui/primitives/popover";

const ONE_DAY_MS = 86400000;

export interface DatePreset {
  label: string;
  getValue: () => { from: Date; to: Date };
}

interface DateRangeFilterModernProps {
  dateFrom: Date | null;
  dateTo: Date | null;
  onDateFromChange: (date: Date | null) => void;
  onDateToChange: (date: Date | null) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  className?: string;
  showRefresh?: boolean;
  customPresets?: DatePreset[];
  maxDate?: Date;
  validationError?: string | null;
}

function getDatePresets(): DatePreset[] {
  return [
    {
      label: strings("descriptions.last7Days"),
      getValue: () => ({ from: subWeeks(new Date(), 1), to: new Date() }),
    },
    {
      label: strings("descriptions.last30Days"),
      getValue: () => ({ from: subMonths(new Date(), 1), to: new Date() }),
    },
    {
      label: strings("descriptions.last3Months"),
      getValue: () => ({ from: subMonths(new Date(), 3), to: new Date() }),
    },
    {
      label: strings("descriptions.thisMonth"),
      getValue: () => ({ from: startOfMonth(new Date()), to: new Date() }),
    },
    {
      label: strings("descriptions.lastMonth"),
      getValue: () => {
        const lastMonth = subMonths(new Date(), 1);
        return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
      },
    },
    {
      label: strings("descriptions.thisYear"),
      getValue: () => ({ from: startOfYear(new Date()), to: new Date() }),
    },
    {
      label: strings("descriptions.lastYear"),
      getValue: () => ({ from: subMonths(new Date(), 12), to: new Date() }),
    },
  ];
}

export function DateRangeFilterModern({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onRefresh,
  isRefreshing = false,
  className,
  showRefresh = true,
  customPresets,
  maxDate,
  validationError,
}: DateRangeFilterModernProps) {
  const [open, setOpen] = useState(false);
  const datePresets = customPresets || getDatePresets();

  const selectedPreset = useMemo(() => {
    if (!dateFrom || !dateTo) return null;

    const fromTime = dateFrom.getTime();
    const toTime = dateTo.getTime();

    for (const preset of datePresets) {
      const { from, to } = preset.getValue();
      const diffFrom = Math.abs(from.getTime() - fromTime);
      const diffTo = Math.abs(to.getTime() - toTime);

      if (diffFrom < ONE_DAY_MS && diffTo < ONE_DAY_MS) {
        return preset.label;
      }
    }
    return null;
  }, [dateFrom, dateTo, datePresets]);

  const displayValue = useMemo(() => {
    if (selectedPreset) return selectedPreset;
    if (dateFrom && dateTo) {
      const locale = dateFnsLocale();
      return `${format(dateFrom, "MMM d", { locale })} - ${format(dateTo, "MMM d, yyyy", { locale })}`;
    }
    return strings("descriptions.selectDateRangePlaceholder");
  }, [selectedPreset, dateFrom, dateTo]);

  const handlePresetSelect = (preset: DatePreset) => {
    const { from, to } = preset.getValue();
    onDateFromChange(from);
    onDateToChange(to);
    setOpen(false);
  };

  return (
    <div className={cn("tw-flex tw-items-center tw-gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild={true}>
          <Button
            variant="outline"
            className="tw-h-10 tw-px-3 tw-gap-2 tw-font-normal"
          >
            <CalendarDays className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            <span className="tw-hidden sm:tw-inline">{displayValue}</span>
            <span className="sm:tw-hidden">
              {selectedPreset ||
                (dateFrom
                  ? format(dateFrom, "MMM d", { locale: dateFnsLocale() })
                  : strings("common.date"))}
            </span>
            <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground tw-ml-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="tw-w-auto tw-p-0 tw-max-w-[95vw]"
          align="end"
          sideOffset={8}
        >
          <div className="tw-flex tw-flex-col md:tw-flex-row tw-max-h-[80vh] tw-overflow-auto">
            {/* Presets - horizontal scroll on mobile, sidebar on desktop */}
            <div
              className={cn(
                "tw-flex tw-gap-1 tw-p-2 tw-border-b tw-border-border tw-overflow-x-auto tw-flex-shrink-0",
                "md:tw-flex-col md:tw-border-b-0 md:tw-border-r md:tw-w-40 md:tw-overflow-visible"
              )}
            >
              <div className="tw-hidden md:tw-block tw-text-xs tw-font-medium tw-text-muted-foreground tw-px-2 tw-py-1.5 tw-uppercase tw-tracking-wider">
                {strings("descriptions.quickSelect")}
              </div>
              <div className="tw-flex tw-gap-1 md:tw-flex-col md:tw-space-y-0.5 md:tw-gap-0">
                {datePresets.map((preset) => (
                  <button
                    type="button"
                    key={preset.label}
                    onClick={() => handlePresetSelect(preset)}
                    className={cn(
                      "tw-whitespace-nowrap tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-md tw-transition-colors",
                      "md:tw-w-full md:tw-px-2",
                      selectedPreset === preset.label
                        ? "tw-bg-primary tw-text-primary-foreground"
                        : "hover:tw-bg-muted tw-text-foreground tw-bg-muted/50 md:tw-bg-transparent"
                    )}
                  >
                    {preset.label}
                    {selectedPreset === preset.label && (
                      <Check className="tw-h-3.5 tw-w-3.5 tw-ml-2 md:tw-ml-0" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Calendar section - stack vertically on mobile */}
            <div className="tw-p-3">
              <div className="tw-text-xs tw-font-medium tw-text-muted-foreground tw-px-2 tw-pb-2 tw-uppercase tw-tracking-wider">
                {strings("descriptions.customRange")}
              </div>
              <div className="tw-flex tw-flex-col lg:tw-flex-row tw-gap-4">
                <div className="tw-w-full">
                  <div className="tw-text-xs tw-text-muted-foreground tw-mb-1 tw-px-1">
                    {strings("descriptions.from")}
                  </div>
                  <Calendar
                    mode="single"
                    selected={dateFrom || undefined}
                    onSelect={(date: Date | undefined) =>
                      onDateFromChange(date || null)
                    }
                    disabled={(date: Date) => {
                      if (maxDate && date > maxDate) return true;
                      if (dateTo && date > dateTo) return true;
                      return false;
                    }}
                    autoFocus={true}
                  />
                </div>
                <div className="tw-w-full">
                  <div className="tw-text-xs tw-text-muted-foreground tw-mb-1 tw-px-1">
                    {strings("descriptions.to")}
                  </div>
                  <Calendar
                    mode="single"
                    selected={dateTo || undefined}
                    onSelect={(date: Date | undefined) =>
                      onDateToChange(date || null)
                    }
                    disabled={(date: Date) => {
                      if (maxDate && date > maxDate) return true;
                      if (dateFrom && date < dateFrom) return true;
                      return false;
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {showRefresh && onRefresh && (
        <Button
          variant="outline"
          size="icon"
          className="tw-h-10 tw-w-10"
          onClick={onRefresh}
          disabled={isRefreshing || Boolean(validationError)}
        >
          <RefreshCw
            className={cn("tw-h-4 tw-w-4", isRefreshing && "tw-animate-spin")}
          />
        </Button>
      )}

      {validationError && (
        <span className="tw-text-xs tw-text-destructive tw-font-medium">
          {validationError}
        </span>
      )}
    </div>
  );
}
